import { createClient } from '@supabase/supabase-js'
import { CareerCard, CareerCardSchema } from '../types/careerCard';
import { env } from './env';
import { queryMonitor } from './queryMonitor';

interface JobApplication {
  id?: string;
  user_id?: string;
  title: string;
  company: string;
  status: string;
  application_date: string;
  last_status_change?: string;
  [key: string]: unknown;
}

interface UserRecord {
  id: string;
  email: string;
  onboarding_complete: boolean;
  dark_mode?: boolean;
  created_at?: string;
  updated_at?: string;
}

const supabaseUrl = env.VITE_SUPABASE_URL;
const supabaseAnonKey = env.VITE_SUPABASE_ANON_KEY;

// Validate URL format before creating client
let validSupabaseUrl = supabaseUrl;
try {
  if (supabaseUrl) {
    new URL(supabaseUrl); // Test if valid URL
  } else {
    throw new Error('Missing Supabase URL in environment variables');
  }
} catch (e) {
  console.error('Invalid Supabase URL format:', supabaseUrl);
  throw new Error('Invalid Supabase URL format');
}

if (!validSupabaseUrl || !supabaseAnonKey) {
  console.error('Missing Supabase environment variables. Please check your .env file.');
  throw new Error('Missing required Supabase environment variables');
}

// Override the global fetch to sanitize URLs in error messages
const originalFetch = window.fetch;
const originalConsoleError = console.error;

// This pattern is used to identify Supabase URLs in error messages
const supabaseUrlPattern = /https?:\/\/[\w-]+\.supabase\.co\/(rest|storage|auth)\/[\w/-]+/g;
const supabaseDomainPattern = /idsakcilnnqhliybglsr\.supabase\.co/g;
const networkErrorPattern = /(ERR_INTERNET_DISCONNECTED|ERR_NETWORK_CHANGED|Failed to fetch)/g;

// Function to sanitize error messages
const sanitizeErrorMessage = (message: string): string => {
  if (typeof message !== 'string') return message;
  
  return message
    .replace(supabaseUrlPattern, '[DATABASE-URL]')
    .replace(supabaseDomainPattern, '[DATABASE-HOST]')
    .replace(networkErrorPattern, 'NETWORK_ERROR');
};

// Create a wrapped version of fetch that suppresses URL errors
const wrappedFetch = async (input: RequestInfo | URL, init?: RequestInit) => {
  // Temporarily override console.error to catch and sanitize network errors
  console.error = function(...args) {
    // Check if this is a network error with URL
    if (args.length > 0 && typeof args[0] === 'string') {
      if (args[0].includes('supabase.co') || 
          args[0].includes('ERR_INTERNET') || 
          args[0].includes('ERR_NETWORK')) {
        
        // For GET/POST/etc. messages that show the full URL
        if (args[0].match(/^(GET|POST|PUT|DELETE|PATCH)\s+https?:\/\/[\w.-]+\.supabase\.co/)) {
          // Don't log these at all
          return;
        }
        
        // Otherwise sanitize the message
        args[0] = sanitizeErrorMessage(args[0]);
      }
    }
    
    // Call original with sanitized args
    originalConsoleError.apply(console, args);
  };
  
  try {
    // Attempt the fetch normally
    return await originalFetch(input, init);
  } catch (error) {
    // Prevent the original error with URL from being logged
    if (error instanceof TypeError || 
        (error instanceof Error && (
          error.message.includes('fetch') || 
          error.message.includes('network') ||
          error.message.includes('internet') ||
          error.message.includes('ERR_INTERNET_DISCONNECTED') ||
          error.message.includes('ERR_NETWORK_CHANGED')
        ))) {
      
      // Create a sanitized error message based on the type of network issue
      let errorMessage = 'No internet connection available';
      
      if (navigator.onLine) {
        errorMessage = 'Failed to connect to the database server';
      }
      
      // Specific error types
      if (error.message.includes('ERR_INTERNET_DISCONNECTED')) {
        errorMessage = 'Internet connection lost';
      } else if (error.message.includes('ERR_NETWORK_CHANGED')) {
        errorMessage = 'Network connection changed';
      }
      
      // Create a sanitized error that doesn't include the URL
      const sanitizedError = new TypeError(errorMessage);
      sanitizedError.name = 'NetworkError';
      
      // Add a code property to help identify network errors
      (sanitizedError as any).code = 'NETWORK_ERROR';
      
      // Add the original stack but with sanitized URLs and simplified
      if (error.stack) {
        const sanitizedStack = 'NetworkError: ' + errorMessage + '\n    at network layer';
        
        Object.defineProperty(sanitizedError, 'stack', {
          value: sanitizedStack
        });
      }
      
      throw sanitizedError;
    }
    
    // For other types of errors, pass through
    throw error;
  } finally {
    // Restore original console.error
    console.error = originalConsoleError;
  }
};

// Create Supabase client with optimized settings
const supabaseConfig: any = {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: false
  },
  db: {
    schema: 'public'
  },
  global: {
    headers: {
      'X-Client-Info': 'CareerCardWeb',
      'Connection': 'keep-alive'
    },
    fetch: (url, options = {}) => {
      // Add connection pooling and timeout optimizations
      const optimizedOptions = {
        ...options,
        signal: options.signal || (new AbortController()).signal,
        keepalive: true,
        // Add request timeout
        timeout: 30000
      };
      
      return wrappedFetch(url, optimizedOptions);
    }
  }
};

// Realtime/WebSocket functionality has been removed - using polling instead

const baseSupabase = createClient(
  validSupabaseUrl,
  supabaseAnonKey,
  supabaseConfig
);

// Wrap Supabase client for development monitoring
let supabase = baseSupabase;

if (import.meta.env.DEV) {
  // Create a proxy to intercept 'from' calls
  supabase = new Proxy(baseSupabase, {
    get(target, prop) {
      if (prop === 'from') {
        return function(table: string) {
          const tableQuery = target.from(table);
          
          // Wrap query methods to add monitoring
          return new Proxy(tableQuery, {
            get(queryTarget, queryProp) {
              const value = queryTarget[queryProp];
              
              // Monitor these specific methods
              if (['select', 'insert', 'update', 'delete', 'upsert'].includes(queryProp as string)) {
                return function(...args: any[]) {
                  const result = value.apply(queryTarget, args);
                  const stack = new Error().stack;
                  const callerLine = stack?.split('\n')[2]; // Get caller location
                  const location = callerLine?.trim() || 'unknown';
                  
                  // Wrap the promise to track timing
                  const originalThen = result.then.bind(result);
                  const startTime = performance.now();
                  
                  result.then = function(onFulfilled?: any, onRejected?: any) {
                    return originalThen(
                      (data: any) => {
                        const duration = performance.now() - startTime;
                        queryMonitor.trackQuery(`${table}.${queryProp}`, location, duration);
                        return onFulfilled?.(data);
                      },
                      (error: any) => {
                        const duration = performance.now() - startTime;
                        queryMonitor.trackQuery(`${table}.${queryProp}`, location, duration);
                        return onRejected?.(error);
                      }
                    );
                  };
                  
                  return result;
                };
              }
              
              // For other methods, return as-is
              return typeof value === 'function' ? value.bind(queryTarget) : value;
            }
          });
        };
      }
      
      // For other properties, return as-is
      const value = target[prop];
      return typeof value === 'function' ? value.bind(target) : value;
    }
  }) as typeof baseSupabase;
}

export { supabase };

// Add connection state tracking
let isConnected = false;
let connectionCheckPromise: Promise<boolean> | null = null;

// Enhanced health check function with connection state caching
export const checkSupabaseConnection = async () => {
  if (connectionCheckPromise) {
    return connectionCheckPromise;
  }

  connectionCheckPromise = new Promise<boolean>((resolve) => {
    if (isConnected) {
      resolve(true);
      return;
    }

    (async () => {
      try {
        const { data, error } = await supabase.from('users').select('count').limit(1);
        isConnected = !error && !!data;
        resolve(isConnected);
      } catch (err) {
        console.error('Supabase connection error:', err);
        isConnected = false;
        resolve(false);
      } finally {
        setTimeout(() => {
          connectionCheckPromise = null;
        }, 5000); // Cache connection check result for 5 seconds
      }
    })();
  });

  return connectionCheckPromise;
};

export const fetchJobApplications = async (userId: string) => {
  const { data, error } = await supabase
    .from('job_applications')
    .select('*')
    .eq('user_id', userId)
    .order('application_date', { ascending: false });

  if (error) throw error;
  return data;
};

export const saveJobApplication = async (userId: string, application: JobApplication) => {
  if (application.id) {
    const { data, error } = await supabase
      .from('job_applications')
      .update(application)
      .eq('id', application.id)
      .select();

    if (error) throw error;
    return data[0];
  } else {
    const { data, error } = await supabase
      .from('job_applications')
      .insert({ ...application, user_id: userId })
      .select();

    if (error) throw error;
    return data[0];
  }
};

export const deleteJobApplication = async (id: string) => {
  const { error } = await supabase
    .from('job_applications')
    .delete()
    .eq('id', id);

  if (error) throw error;
};

export const fetchCareerCardSchemas = async (): Promise<CareerCardSchema[]> => {
  const { data, error } = await supabase
    .from('career_card_schemas')
    .select('*')
    .order('version', { ascending: false });

  if (error) {
    console.error('Error fetching career card schemas:', error);
    throw error;
  }

  return data || [];
};

export const getLatestCareerCardSchema = async (): Promise<CareerCardSchema | null> => {
  const { data, error } = await supabase
    .from('career_card_schemas')
    .select('*')
    .order('version', { ascending: false })
    .limit(1)
    .single();

  if (error) {
    console.error('Error fetching latest career card schema:', error);
    throw error;
  }

  return data;
};

export const fetchTimelineData = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('No user logged in');

  // Fetch all types of data
  const [notes, careerCards, files, reviews, workExperiences] = await Promise.all([
    supabase.from('notes').select('*').eq('user_id', user.id),
    supabase.from('career_cards').select('*').eq('user_id', user.id),
    supabase
      .from('files')
      .select('*')
      .eq('user_id', user.id)
      .eq('usage->>type', 'resume')
      .is('deleted_at', null),
    supabase.from('reviews').select('*').eq('user_id', user.id),
    supabase.from('work_experience').select('*').eq('user_id', user.id)
  ]);

  // Combine and format the data with null checks
  const timelineData = [
    ...(notes.data?.map(note => ({
      id: note.id,
      type: 'note',
      title: note.title,
      start_date: note.note_date,
      data: note
    })) || []),
    ...(careerCards.data?.map(card => ({
      id: card.cardid,
      type: 'career_card',
      title: card.title,
      start_date: card.start_date,
      end_date: card.end_date,
      data: card
    })) || []),
    ...(files.data?.map(file => ({
      id: file.id,
      type: 'resume',
      title: file.file_name,
      start_date: file.created_at,
      data: file
    })) || []),
    ...(reviews.data?.map(review => ({
      id: review.id,
      type: 'review',
      title: review.review_name,
      start_date: review.created_at,
      data: review
    })) || []),
    ...(workExperiences.data?.map(exp => ({
      id: exp.id,
      type: 'work_experience',
      title: exp.position,
      start_date: exp.start_date,
      end_date: exp.end_date,
      data: exp
    })) || [])
  ];

  return timelineData.sort((a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime());
};

// Helper function to get user record
export const getUserRecord = async (userId: string) => {
  const { data, error } = await supabase
    .from('users')
    .select('*')
    .eq('id', userId)
    .maybeSingle();
  
  return { data: data as UserRecord | null, error };
};

// Helper function to create user record
export const createUserRecord = async (userId: string, email: string, onboardingComplete: boolean = false) => {
  const { data, error } = await supabase
    .from('users')
    .insert([{
      id: userId,
      email,
      onboarding_complete: onboardingComplete
    }])
    .select()
    .single();
  
  return { data: data as UserRecord | null, error };
};

// Helper function to update user record
export const updateUserRecord = async (userId: string, updates: Partial<UserRecord>) => {
  const { data, error } = await supabase
    .from('users')
    .update(updates)
    .eq('id', userId)
    .select()
    .single();

  if (error) throw error;
  return data;
};

// New function to support enhanced signup with demographic data
export interface SignUpWithProfileData {
  email: string;
  password: string;
  firstName?: string;
  lastName?: string;
  company?: string;
  city?: string;
  state?: string;
  country?: string;
  timezone?: string;
  redirectTo?: string;
}

export const signUpWithProfile = async (signUpData: SignUpWithProfileData) => {
  const { email, password, firstName, lastName, company, city, state, country, timezone, redirectTo } = signUpData;
  
  try {
    // Create metadata with the exact field names expected by the trigger function
    const metadata = {
      first_name: firstName || '',
      last_name: lastName || '',
      company: company || '',
      city: city || '',
      state: state || '',
      country: country || '',
      timezone: timezone || 'UTC', // Add timezone to metadata with UTC default
      user_type: 'free',
      acquisition_code: 'emp'
    };
    
    // Let Supabase handle creating the user record through its trigger
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectTo || `${window.location.origin}/home`,
        data: metadata
      }
    });
    
    if (error) {
      console.error('Signup error:', error);
      throw new Error(`Auth signup failed: ${error.message}`);
    }
    
    // Log success for debugging
    if (data?.user) {
      console.log('User signup successful:', data.user.id);
      
      // Create initial user settings with timezone
      try {
        await supabase
          .from('user_settings')
          .upsert({
            user_id: data.user.id,
            email: email.toLowerCase(),
            settings: {
              darkMode: false,
              defaultCareerCardsView: 'grid',
              sidebarCollapsed: false,
              timezone: timezone || 'UTC'
            }
          });
      } catch (settingsError) {
        // Just log the error but don't fail the signup
        console.error('Error setting initial user settings:', settingsError);
      }

      // Publish user registration event for notifications
      try {
        // Import the notification integration service
        const { NotificationEvents } = await import('../services/notificationsV2Integration');
        
        await NotificationEvents.userRegistered(data.user.id, {
          email: email.toLowerCase(),
          registration_method: 'email',
          source: 'web'
        });
        
        console.log('User registration notification event published');
      } catch (notificationError) {
        // Don't fail signup if notification fails
        console.warn('Failed to publish user registration notification:', notificationError);
      }
    } else {
      console.warn('Signup succeeded but no user data returned');
    }
    
    return data;
  } catch (error) {
    console.error('Signup process error:', error);
    
    // Re-throw with a more descriptive message
    if (error instanceof Error) {
      throw new Error(`Signup failed: ${error.message}`);
    } else {
      throw new Error('Signup failed: Unknown error occurred');
    }
  }
};

// Add logging in development mode
if (env.DEV) {
  // ... existing code ...
}

// Connection pool management
let connectionPool: Map<string, Promise<any>> = new Map();
let poolCleanupInterval: ReturnType<typeof setTimeout> | null = null;

// Initialize connection pool cleanup
const initializeConnectionPool = () => {
  if (poolCleanupInterval) return;
  
  poolCleanupInterval = setInterval(() => {
    // Clear old connections every 5 minutes
    connectionPool.clear();
  }, 5 * 60 * 1000);
};

// Request batching utility
interface BatchRequest {
  table: string;
  operation: 'select' | 'insert' | 'update' | 'delete';
  query: any;
  resolve: (value: any) => void;
  reject: (error: any) => void;
}

let batchQueue: BatchRequest[] = [];
let batchTimeout: ReturnType<typeof setTimeout> | null = null;

const processBatch = async () => {
  if (batchQueue.length === 0) return;
  
  const currentBatch = [...batchQueue];
  batchQueue = [];
  
  // Group by table and operation for efficient batching
  const grouped = currentBatch.reduce((acc, req) => {
    const key = `${req.table}-${req.operation}`;
    if (!acc[key]) acc[key] = [];
    acc[key].push(req);
    return acc;
  }, {} as Record<string, BatchRequest[]>);
  
  // Process each group
  for (const [key, requests] of Object.entries(grouped)) {
    try {
      // For now, execute individually but could be optimized further
      for (const req of requests) {
        try {
          const result = await req.query;
          req.resolve(result);
        } catch (error) {
          req.reject(error);
        }
      }
    } catch (error) {
      requests.forEach(req => req.reject(error));
    }
  }
};

// Batch request helper
export const batchRequest = <T>(
  table: string,
  operation: 'select' | 'insert' | 'update' | 'delete',
  query: Promise<T>
): Promise<T> => {
  return new Promise((resolve, reject) => {
    batchQueue.push({ table, operation, query, resolve, reject });
    
    if (batchTimeout) clearTimeout(batchTimeout);
    batchTimeout = setTimeout(processBatch, 10); // Batch requests within 10ms
  });
};

// Initialize optimizations
initializeConnectionPool();

// Performance monitoring wrapper
export const monitorQuery = async <T>(
  table: string,
  operation: string,
  queryFn: () => Promise<T>
): Promise<T> => {
  const startTime = performance.now();
  let success = false;
  let error: Error | null = null;
  
  try {
    const result = await queryFn();
    success = true;
    return result;
  } catch (err) {
    success = false;
    error = err instanceof Error ? err : new Error(String(err));
    throw err;
  } finally {
    const duration = performance.now() - startTime;
    
    // Track in development query monitor
    if (import.meta.env.DEV) {
      // Get caller location from stack trace
      const stack = new Error().stack;
      const callerLine = stack?.split('\n')[3]; // Skip Error, monitorQuery, and the wrapper
      const location = callerLine?.trim() || 'unknown';
      
      queryMonitor.trackQuery(`${table}.${operation}`, location, duration);
    }
    
    // Send performance metrics to Statsig for developer analysis
    try {
      if (typeof window !== 'undefined' && (window as any).statsig) {
        const statsig = (window as any).statsig;
        
        // Track all database queries for performance analysis
        statsig.logEvent('database_query_performance', {
          table,
          operation,
          duration_ms: Math.round(duration),
          success,
          error_type: error?.name || null,
          error_message: error?.message || null,
          timestamp: Date.now(),
          // Add context for better analysis
          user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
          connection_type: typeof navigator !== 'undefined' && (navigator as any).connection 
            ? (navigator as any).connection.effectiveType 
            : 'unknown'
        });
        
        // Specifically track slow queries for immediate attention
        if (duration > 1000) {
          statsig.logEvent('database_slow_query', {
            table,
            operation,
            duration_ms: Math.round(duration),
            success,
            error_type: error?.name || null,
            severity: duration > 5000 ? 'critical' : duration > 3000 ? 'high' : 'medium',
            timestamp: Date.now()
          });
        }
        
        // Track database errors separately for error analysis
        if (!success && error) {
          statsig.logEvent('database_query_error', {
            table,
            operation,
            duration_ms: Math.round(duration),
            error_type: error.name,
            error_message: error.message,
            timestamp: Date.now()
          });
        }
      }
    } catch (statsigError) {
      // Don't let Statsig errors affect database operations
      console.debug('Failed to log database metrics to Statsig:', statsigError);
    }
    
    // Keep development logging for immediate feedback
    if (env.DEV && duration > 1000) {
      console.warn(`Slow query detected: ${table}.${operation} took ${duration.toFixed(2)}ms`);
    }
  }
};

// Remove the local memory storage since we're sending to Statsig
// Keep getQueryMetrics for backward compatibility but make it return empty data
export const getQueryMetrics = () => {
  console.warn('getQueryMetrics is deprecated. Database performance metrics are now sent to Statsig.');
  return {
    totalQueries: 0,
    averageDuration: 0,
    slowQueries: 0,
    errorRate: 0,
    byTable: {}
  };
};

// Optimized query helpers
export const optimizedSelect = async (table: string, query: any) => {
  return monitorQuery(table, 'select', async () => {
    const cacheKey = `${table}-${JSON.stringify(query)}`;
    
    // Check connection pool for similar queries
    if (connectionPool.has(cacheKey)) {
      return connectionPool.get(cacheKey);
    }
    
    const queryPromise = supabase.from(table).select(query);
    connectionPool.set(cacheKey, queryPromise);
    
    return queryPromise;
  });
};